import { useMemo, useCallback } from 'react'
import { SimpleSelectOption } from '@workwave-tidal/core/components'
import { useTidalFormBridge } from '@workwave-tidal/core/form'

import * as Texts from '../../../../../../../intl'
import * as OrderTexts from '../../../../intl'

import type { ExecutionEventsFormValues } from '../../../../typings'
import type { Props } from '../../typings'

import { form } from '../../../../formState'

import { Barcodes, type MergedBarcodePod } from './components/Barcodes'

// Status order priority: SCANNED first, then others
const statusOrder: Record<string, number> = {
  SCANNED: 0,
  MISSING_BARCODE: 1,
  MISSING_PACKAGE: 2,
  UNREADABLE: 3,
  UNSCANNED: 4,
}

export function PodBarcodes(props: Props) {
  const { accountPreferences } = props

  const {
    fieldInfo: { value: barcodes, onChange: onBarcodesChange },
  } = useTidalFormBridge<
    uui.domain.client.rm.BarcodePod[],
    'formPods.barcodes',
    ExecutionEventsFormValues
  >({
    name: 'formPods.barcodes',
    initialValue: [],
  })

  const {
    fieldInfo: { value: driverBarcodes },
  } = useTidalFormBridge<
    uui.domain.client.rm.DriverBarcodesPod[],
    'formPods.driverBarcodes',
    ExecutionEventsFormValues
  >({
    name: 'formPods.driverBarcodes',
    initialValue: [],
  })

  const options: SimpleSelectOption<string>[] = [
    {
      value: 'SCANNED',
      label: Texts.getBarcodesStatusText('SCANNED'),
    },
    {
      value: 'MISSING_BARCODE',
      label: Texts.getBarcodesStatusText('MISSING_BARCODE'),
    },
    {
      value: 'MISSING_PACKAGE',
      label: Texts.getBarcodesStatusText('MISSING_PACKAGE'),
    },
    {
      value: 'UNREADABLE',
      label: Texts.getBarcodesStatusText('UNREADABLE'),
    },
    {
      value: 'UNSCANNED',
      label: Texts.getBarcodesStatusText('UNSCANNED'),
    },
  ]

  // Driver barcodes show just "Scanned" - "added from driver" is shown in the label
  const driverBarcodeOptions: SimpleSelectOption<string>[] = [
    {
      value: 'SCANNED',
      label: Texts.getBarcodesStatusText('SCANNED'),
    },
  ]

  // Merge and sort barcodes: regular barcodes + driver barcodes, scanned first
  const mergedBarcodes = useMemo(() => {
    const regularBarcodes: MergedBarcodePod[] = (barcodes || []).map(b => ({
      ...b,
      isDriverBarcode: false,
    }))

    const driverBarcodesMerged: MergedBarcodePod[] = (driverBarcodes || []).map(b => ({
      ...b,
      isDriverBarcode: true,
    }))

    const all = [...regularBarcodes, ...driverBarcodesMerged]
    // Sort by status (scanned first)
    return all.sort((a, b) => {
      const orderA = statusOrder[a.barcodeStatus] ?? 99
      const orderB = statusOrder[b.barcodeStatus] ?? 99
      return orderA - orderB
    })
  }, [barcodes, driverBarcodes])

  const handleOnChange = useCallback(
    (pod: uui.domain.client.rm.BarcodePod, index: number) => {
      // Find the original barcode in regular barcodes (driver barcodes are read-only)
      const mergedPod = mergedBarcodes[index]
      if (mergedPod.isDriverBarcode) return // Should not happen as driver barcodes are disabled

      // Find and update the original barcode in the regular barcodes array
      const originalIndex = barcodes.findIndex(
        b => b.barcode === mergedPod.barcode && b.uuid === mergedPod.uuid,
      )

      if (originalIndex !== -1) {
        const updatedBarcodes = barcodes.map((b, i) => (i === originalIndex ? { ...pod } : b))
        onBarcodesChange(updatedBarcodes)
      }
    },
    [mergedBarcodes, barcodes, onBarcodesChange],
  )

  return (
    <>
      <form.SectionTitle variant="alternative">
        {OrderTexts.getBarcodesFieldText()}
      </form.SectionTitle>
      <Barcodes
        onChange={handleOnChange}
        value={mergedBarcodes}
        testId="pod-barcodes"
        fieldDisabled={false}
        options={options}
        accountPreferences={accountPreferences}
        driverBarcodeOptions={driverBarcodeOptions}
        driverBarcodeLabel={Texts.getDriverBarcodesFieldText()}
        driverBarcodeTooltip={Texts.getDriverBarcodesTooltipText()}
      />
    </>
  )
}
